import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Container,
  RadioGroup,
  FormControl,
  FormControlLabel,
  styled,
  TextField,
  useMediaQuery,
  Checkbox,
  ThemeProvider,
} from "@mui/material";
import PostSurveyView from "./PostSurvey";
import { createTheme } from "@mui/material/styles";

const primaryColor = "#B7BCA1";

const StyledContainer = styled(Container)({
  padding: 0,
  textAlign: "center",
  width: "325px",
  transition: "transform 0.5s ease",
});

const QuestionContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  marginTop: 0,
});

const OptionButton = styled(Button)(({ theme, selected }) => ({
  marginTop: 10,
  marginBottom: 5,
  width: "350px",
  height: 45,
  padding: theme.spacing(2),
  fontFamily: "'magnat'",
  textAlign: "center",
  borderRadius: 20,
  border: `1px solid ${primaryColor}`,
  backgroundColor: selected ? primaryColor : "inherit",
  color: selected ? "white" : "black",
  "&:hover": {
    backgroundColor: selected ? primaryColor : theme.palette.action.hover,
  },
  marginLeft: "27px",
}));

const ButtonsContainer = styled("div")({
  marginTop: 5,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 5,
});

const NextButton = styled(Button)({
  width: "165px",
  height: 45,
  margin: "8px",
  borderRadius: 20,
  color: "white",
  border: `1px solid ${primaryColor}`,
  fontFamily: "'magnat'",
});

const StyledTextField = styled(TextField)({
  margin: "8px",
  width: "80%",
  "& label.Mui-focused": {
    color: "black",
  },
});

const Survey = ({ onComplete }) => {
  const [showPostSurveyView, setShowPostSurveyView] = useState(false);
  const [showEmailStep, setShowEmailStep] = useState(false);
  const [email, setEmail] = useState("");
  const [validEmail, setValidEmail] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(true);

  useEffect(() => {
    if (localStorage.getItem("prompt")) localStorage.removeItem("prompt");
    if (localStorage.getItem("email")) localStorage.removeItem("email");
  }, []);

  const initialFormData = {
    steps: [
      {
        question:
          "Welcome to flânerie. Let’s find your essentials. But first tell us...",
        options: [
          {
            label: "I am a flânerie enthusiast",
            value: "I am a flânerie enthusiast",
          },
          {
            label: "I am new here",
            value: "I am new here",
          },
        ],
      },
      {
        question:
          "Seeking specific skin benefits? Tell us what you're looking for:",
        options: [
          {
            label: "PROTECTIVE MAINTENANCE",
            value: "Protective Maintenance",
          },
          {
            label: "DRYNESS/DEHYDRATION",
            value: "Dryness/Dehydration",
          },
          {
            label: "DULNESS OR LACKLUSTRE TONE",
            value: "Dullness or lacklustre tone",
          },
          {
            label: "GENERAL SIGNS OF AGEING",
            value: "General signs of ageing",
          },
          {
            label: "SIGNS OF CONGESTION",
            value: "Signs of Congestion",
          },
        ],
      },
      {
        question: "Could you describe your skin type?",
        options: [
          { label: "SENSITIVE", value: "Sensitive" },
          { label: "REACTIVE", value: "Reactive" },
          { label: "DRY", value: "Dry" },
          { label: "COMBINATION TO OILY", value: "Combination to Oily" },
        ],
      },
      {
        question: "Any concerns with the eye area we should know about?",
        options: [
          { label: "DARK CIRCLES", value: "Dark circles" },
          {
            label: "FINE LINES AND WRINKLES",
            value: "Fine lines and wrinkles",
          },
          { label: "UNDER EYE PUFFINESS", value: "Under eye puffiness" },
          { label: "ALL OF THEM", value: "All of them" },
          { label: "NONE OF THEM", value: "None of them" },
        ],
      },
    ],
  };

  const { control, handleSubmit } = useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const onSubmit = async () => {
    if (showEmailStep) {
      setShowPostSurveyView(true);
      localStorage.setItem("email", email);
      localStorage.setItem(
        "prompt",
        `question: How does your skin feel during the day? answer:${answers[0].selectedOption}; 
        question: What is your skin goal? answer: ${answers[1].selectedOption}; 
        question: How do you like your skincare to feel? answer: ${answers[2].selectedOption}`
      );

      // Save sendRoutine to localStorage based on the checkbox state
      localStorage.setItem("sendRoutine", isSubscribed ? "true" : "false");

      try {
        await fetch("https://mid-code.onrender.com/save-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
      } catch (error) {
        console.error(error);
      }
    } else {
      setShowEmailStep(true);
    }
  };

  const handleOptionSelect = (selectedOption) => {
    setAnswers((prevAnswers) => {
      const newAnswers = [...prevAnswers];
      newAnswers[currentStep] = { selectedOption };
      return newAnswers;
    });
  };

  const handleNext = () => {
    if (answers[currentStep]?.selectedOption) {
      setSwipeDirection("left");
      setTimeout(() => {
        setSwipeDirection("right");
        setCurrentStep((prevStep) =>
          prevStep === initialFormData.steps.length - 1 ? 0 : prevStep + 1
        );
        setTimeout(() => setSwipeDirection(null), 0);
      }, 500);
    }
  };

  const handlePrev = () => {
    setSwipeDirection("right");
    setTimeout(() => {
      setSwipeDirection("left");
      setCurrentStep((prevStep) =>
        prevStep === 0 ? initialFormData.steps.length - 1 : prevStep - 1
      );
      setTimeout(() => setSwipeDirection(null), 0);
    }, 500);
  };

  const handleEmailChange = (e) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setValidEmail(emailRegex.test(emailValue));
  };

  const theme = createTheme({
    palette: { primary: { main: "#000000" } },
  });

  const handleCancel = () => setShowPostSurveyView(false);

  return (
    <StyledContainer
      maxWidth="sm"
      style={{
        transform: `translateX(${
          swipeDirection === "left"
            ? "200%"
            : swipeDirection === "right"
            ? "-200%"
            : "0"
        })`,
        fontFamily: "'magnat'",
      }}
    >
      {showPostSurveyView ? (
        <PostSurveyView onCancel={handleCancel} onComplete={onComplete} />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <QuestionContainer>
            {!showEmailStep ? (
              <>
                <p
                  style={{
                    fontSize: "24px",
                    marginBottom: 20,
                    fontFamily: "'magnat'",
                    fontWeight: 500,
                    textTransform: "none",
                  }}
                >
                  {initialFormData.steps[currentStep].question}
                </p>
                <FormControl component="fieldset">
                  <RadioGroup
                    control={control}
                    name={`steps[${currentStep}].selectedOption`}
                    value={answers[currentStep]?.selectedOption || ""}
                    onChange={(e) => handleOptionSelect(e.target.value)}
                  >
                    {initialFormData.steps[currentStep].options.map(
                      (option, optionIndex) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={
                            <OptionButton
                              selected={
                                option.value ===
                                answers[currentStep]?.selectedOption
                              }
                              onClick={() => handleOptionSelect(option.value)}
                            >
                              {option.label}
                            </OptionButton>
                          }
                          label={null}
                        />
                      )
                    )}
                  </RadioGroup>
                </FormControl>
                <ButtonsContainer>
                  <NextButton
                    variant="outlined"
                    onClick={handlePrev}
                    disabled={currentStep === 0}
                    sx={{
                      color: "black",
                      "&:hover": {
                        color: primaryColor,
                        border: `1px solid ${primaryColor}`,
                        "&.Mui-disabled": {
                          color: primaryColor,
                        },
                      },
                    }}
                  >
                    Back
                  </NextButton>

                  {currentStep < initialFormData.steps.length - 1 && (
                    <NextButton
                      variant="contained"
                      sx={{
                        backgroundColor: primaryColor,
                        "&:hover": {
                          backgroundColor: primaryColor,
                        },
                        color: "white",
                        "&.Mui-disabled": {
                          background: "#b4b8a3",
                          color: "#9E9595 ",
                        },
                        border: "none",
                        fontFamily: "'magnat'",
                      }}
                      onClick={handleNext}
                      disabled={!answers[currentStep]?.selectedOption}
                    >
                      Next
                    </NextButton>
                  )}
                  {currentStep === initialFormData.steps.length - 1 && (
                    <NextButton
                      variant="contained"
                      type="submit"
                      sx={{
                        backgroundColor: primaryColor,
                        "&:hover": {
                          backgroundColor: primaryColor,
                        },
                        color: "white",
                        "&.Mui-disabled": {
                          background: "#b4b8a3",
                          color: "#9E9595",
                        },
                        border: "none",
                        fontFamily: "'magnat'",
                      }}
                      disabled={!answers[currentStep]?.selectedOption}
                    >
                      Continue
                    </NextButton>
                  )}
                </ButtonsContainer>
              </>
            ) : (
              <ThemeProvider theme={theme}>
                <p
                  style={{
                    fontSize: "30px",
                    marginBottom: 20,
                    fontFamily: "'neue_hans_kendrickregular'",
                  }}
                >
                  Enter your email to continue
                </p>

                <StyledTextField
                  id="standard-required"
                  label="Email"
                  variant="standard"
                  value={email}
                  onChange={handleEmailChange}
                  helperText={!validEmail ? "Invalid email" : ""}
                  sx={{
                    marginBottom: 5,
                    width: 335,
                  }}
                />

                <ButtonsContainer>
                  <NextButton
                    variant="outlined"
                    onClick={() => setShowEmailStep(false)}
                  >
                    Back
                  </NextButton>
                  <NextButton
                    variant="contained"
                    type="submit"
                    disabled={!validEmail}
                    sx={{
                      backgroundColor: "black",
                      color: "white",
                    }}
                  >
                    Continue
                  </NextButton>
                </ButtonsContainer>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSubscribed}
                      onChange={() => setIsSubscribed(!isSubscribed)}
                    />
                  }
                  label="Email me the routine after scan"
                  sx={{
                    fontFamily: "'neue_hans_kendrickregular'",
                    marginTop: 5,
                  }}
                />
              </ThemeProvider>
            )}
          </QuestionContainer>
        </form>
      )}
    </StyledContainer>
  );
};

export default Survey;
